'use client';

import React from 'react';
import { motion } from 'framer-motion';
import useGetHeroImageByTag from '@/components/hooks/use-get-hero-image-by-tag';
import { Image } from '@/components/ui/Image';
import { MotionLink } from '@/components/ui/Motion';
import { StaggeredText } from '@/components/ui/StaggeredText';
import { TypographyH2, TypographySmall } from '@/components/ui/typography';
import { Skeleton } from '@/components/ui/skeleton';
import { cls } from '@/lib/utils';
import SectionBase from '../section-base';
import {
  FLOOR_PLANS_TITLE,
  FEATURED_FLOOR_PLANS,
  type FeaturedFloorPlan,
} from './consts';

const SECTION_ID = 'floor-plans-spotlight';

const wrapperMotionProps = {
  initial: 'hidden' as const,
  whileInView: 'visible' as const,
  viewport: { once: true },
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  },
};

const cardMotionProps = {
  variants: {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  },
};

function FloorPlanCard({ plan }: { plan: FeaturedFloorPlan }) {
  const { name, details, slug } = plan;
  const tag = `hero_floor_plans_${name.replace(/\s+/g, '_')}`;
  const { data: heroImage, isLoading } = useGetHeroImageByTag({ tag });

  return (
    <motion.div className="flex flex-col gap-2" {...cardMotionProps}>
      <MotionLink href={`/floor-plans/${slug}`} className="block">
        {isLoading || !heroImage ? (
          <Skeleton className="w-full aspect-4/3 rounded-lg" />
        ) : (
          <Image
            src={heroImage}
            alt={`${name} floor plan rendering`}
            fill
            sizes="(max-width: 768px) 100vw, 50vw"
            className="rounded-lg object-cover"
            wrapperClassName="aspect-4/3 relative w-full h-auto"
            style={{ objectFit: 'cover', aspectRatio: '4/3' }}
          />
        )}
      </MotionLink>

      <div className="text-center">
        <TypographySmall className="font-medium">
          {name} {details.bedrooms} bed {details.bathrooms} bath
        </TypographySmall>
        <p className="text-sm text-muted-foreground font-prata">
          {details.sqft.toLocaleString()} sq ft
        </p>
      </div>
    </motion.div>
  );
}

function FloorPlansSection() {
  return (
    <SectionBase
      id={SECTION_ID}
      containerProps={{ id: SECTION_ID }}
      background="bg-background"
    >
      <motion.div
        className={cls`flex flex-col items-center gap-10`}
        {...wrapperMotionProps}
      >
        {/* Title */}
        <TypographyH2 className={cls`text-center border-b-0`}>
          <StaggeredText staggerChildren={0.03} delay={0.1}>
            {FLOOR_PLANS_TITLE}
          </StaggeredText>
        </TypographyH2>

        {/* 2x2 grid of floor plan cards */}
        <div className={cls`grid grid-cols-1 sm:grid-cols-2 gap-6 w-full`}>
          {FEATURED_FLOOR_PLANS.map(plan => (
            <FloorPlanCard key={plan.name} plan={plan} />
          ))}
        </div>
      </motion.div>
    </SectionBase>
  );
}

export default FloorPlansSection;
